import { NavLink } from "react-router-dom";
import { OfflineBanner } from "@/components/OfflineBanner";
import { UndoDeleteButton } from "@/components/UndoDeleteButton";
import { LogOut, Sun, CalendarDays, BarChart3, Settings } from "lucide-react";
import { useAuth } from "@/lib/auth";
import SyncIndicator from "@/components/SyncIndicator";

const linkBase = "px-2 md:px-4 py-1.5 rounded-full text-[12px] md:text-sm font-medium transition-colors duration-200";

const TABS = [
  { to: "/dashboard", label: "Giorno", icon: Sun, id: "day", end: true },
  { to: "/settimana", label: "Settimana", icon: CalendarDays, id: "week" },
  { to: "/report", label: "Report", icon: BarChart3, id: "report" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, id: "settings" }
];

function BottomTabBar() {
  return (
    <nav className="tl-tabbar md:hidden fixed left-0 right-0 bottom-0 z-40 grid grid-cols-5 bg-[#FDFDFB]/95 backdrop-blur-lg border-t border-[#E4E7E1]" data-testid="bottom-tabbar">
      {TABS.map(({ to, label, icon: Icon, id, end }) => (
        <NavLink
          key={id}
          to={to}
          end={end}
          data-testid={`tab-${id}`}
          className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 pt-1.5 pb-1 text-[11px] transition-colors ${isActive ? "text-[#3B443D] font-semibold" : "text-[#6A7368] font-medium"}`}
        >
          {({ isActive }) => (
            <>
              <span className={`w-14 h-8 rounded-full flex items-center justify-center transition-colors duration-200 ${isActive ? "bg-[#D8E0D2]" : ""}`}>
                <Icon size={20} strokeWidth={1.75} />
              </span>
              {label}
            </>
          )}
        </NavLink>
      ))}
      <UndoDeleteButton variant="tab" />
    </nav>
  );
}

export default function Layout({ children }) {
  const { logout, user } = useAuth();

  return (
    <div className="min-h-screen [overflow-x:clip]">
      <header className="sticky top-0 z-30 bg-[#FDFDFB]/95 backdrop-blur border-b border-[#E4E7E1]" style={{ paddingTop: "env(safe-area-inset-top)" }} data-testid="app-header">
        <div className="max-w-[1400px] mx-auto px-3 md:px-8 py-2 md:py-3 flex flex-wrap items-center justify-between gap-2">
          {/* Brand */}
          <a
            href="/dashboard"
            className="group inline-flex items-center gap-2 md:gap-3 pl-1 pr-2 md:pr-3 py-1 rounded-full hover:bg-[#F3F5F0] transition-colors min-w-0"
            data-testid="brand-logo"
          >
            <span className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center bg-[#A3B897]/25 overflow-hidden ring-1 ring-[#E4E7E1] flex-shrink-0">
              <img src="/logo.jpg" alt="Tempo leggero" className="w-full h-full object-cover" />
            </span>
            <span className="flex flex-col leading-tight min-w-0">
              <span className="font-serif-display font-semibold text-[15px] md:text-[20px] text-[#3B443D] truncate" data-testid="brand-name">
                Tempo leggero
              </span>
              <span className="hidden md:inline text-[11px] text-[#6A7368] italic">un'idea di Silvia Lanfranchi</span>
            </span>
          </a>

          <div className="flex items-center gap-1.5 md:gap-2">
            <nav className="hidden md:flex items-center gap-0.5 md:gap-1" data-testid="main-nav">
              <NavLink to="/dashboard" end data-testid="nav-day" className={({ isActive }) => `${linkBase} ${isActive ? "tl-nav-active" : "text-[#58635B] hover:text-[#3B443D]"}`}>Giorno</NavLink>
              <NavLink to="/settimana" data-testid="nav-week" className={({ isActive }) => `${linkBase} ${isActive ? "tl-nav-active" : "text-[#58635B] hover:text-[#3B443D]"}`}>Settimana</NavLink>
              <NavLink to="/report" data-testid="nav-report" className={({ isActive }) => `${linkBase} ${isActive ? "tl-nav-active" : "text-[#58635B] hover:text-[#3B443D]"}`}>Report</NavLink>
              <NavLink to="/impostazioni" data-testid="nav-settings" className={({ isActive }) => `${linkBase} ${isActive ? "tl-nav-active" : "text-[#58635B] hover:text-[#3B443D]"}`}>Impostazioni</NavLink>
            </nav>
            <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
              <SyncIndicator />
              {user?.picture && <img src={user.picture} alt="" className="hidden md:block w-8 h-8 rounded-full ring-1 ring-[#E4E7E1]" />}
              <button onClick={logout} className="tl-btn-soft px-2.5 md:px-3 py-1.5 rounded-full bg-white border border-[#E4E7E1] text-sm text-[#58635B] hover:bg-[#F3F5F0] inline-flex items-center gap-1.5" data-testid="logout-btn" aria-label="Esci">
                <LogOut size={14} strokeWidth={1.5} />
                <span className="hidden md:inline">Esci</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-3 md:px-10 py-5 md:py-8">
        {children}
      </main>
      <footer className="max-w-[1400px] mx-auto px-3 md:px-10 py-6 md:py-8 text-center text-xs text-[#6A7368] font-serif-display italic tl-tabbar-space">
        Tempo leggero — un'idea di Silvia Lanfranchi.
      </footer>
      <BottomTabBar />
      <OfflineBanner />
      <UndoDeleteButton />
    </div>
  );
}
